import { PictureInfo } from '../../apis/serviceHistoryApi';
import { Box, Grid, Link, Typography } from '@mui/material';
import PictureDialog from './PictureDialog';
import React, { useEffect } from 'react';

type PictureListProps = {
  selectedPictureId?: string | null;
  pictures: PictureInfo[];
  onImageClicked?: (picture: PictureInfo) => void;
  onImageClosed?: () => void;
};

const PictureList: React.FC<PictureListProps> = ({ pictures, onImageClicked = null, onImageClosed = null, selectedPictureId = null }) => {
  const [pictureToShow, setPictureToShow] = React.useState<PictureInfo | null>(null);
  const [pictureIndex, setPictureIndex] = React.useState(0);

  useEffect(() => {
    if (selectedPictureId) {
      const picIndex = pictures.findIndex((x) => x.id === selectedPictureId);
      if (picIndex >= 0) {
        const picture = pictures[picIndex];
        if (picture) {
          onImageClickedInternal(picture, picIndex);
        }
      }
    }
  }, [selectedPictureId]);

  const onImageClickedInternal = (picture: PictureInfo, index: number) => {
    setPictureIndex(index);

    setPictureToShow(picture);
    if (onImageClicked) {
      onImageClicked(picture);
    }
  };

  const changePictureIndex = (index: number) => {
    setPictureIndex(index);
    if (index < 0 || index >= pictures.length) {
      setPictureToShow(null);
    } else {
      setPictureToShow(pictures[index]);
    }
  };

  return (
    <>
      <Grid container sx={{ pt: 1, pb: 0 }} spacing={2}>
        {pictures
          .sort((x, y) => x.sequence - y.sequence)
          .map((item, index) => (
            <Grid item key={`image_${index}`}>
              <Box
                overflow="hidden"
                whiteSpace="nowrap"
                sx={{
                  maxWidth: 150,
                }}
              >
                <Link
                  onClick={() => onImageClickedInternal(item, index)}
                  sx={{
                    maxHeight: 150,
                    maxWidth: 150,
                    minWidth: 100,
                    minHeight: 100,
                    p: 1,
                    cursor: 'pointer',
                    border: '2px solid',
                    borderColor: (theme) => theme.palette.divider,
                    '&:hover': {
                      borderColor: (theme) => theme.palette.action.active,
                    },
                  }}
                  display="block"
                  component="img"
                  src={item.photoUrl}
                  alt={'Pool service photo'}
                  loading="lazy"
                  title="View photo"
                />
                {item.caption && (
                  <Box sx={{ display: 'flex' }}>
                    <Typography
                      sx={{ flexGrow: 1, width: 0 }}
                      mt={1}
                      display="block"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      textAlign="center"
                      variant="caption"
                      color="text.secondary"
                      title={item.caption}
                    >
                      {item.caption}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          ))}
      </Grid>
      <PictureDialog
        picture={pictureToShow}
        onCloseClicked={() => {
          setPictureToShow(null);
          if (onImageClosed) onImageClosed();
        }}
        onPreviousClicked={() => changePictureIndex(pictureIndex - 1)}
        onNextClicked={() => changePictureIndex(pictureIndex + 1)}
        hasPrevious={pictureIndex > 0}
        hasNext={pictureIndex < pictures.length - 1}
      />
    </>
  );
};

export default PictureList;
